import * as React from "react";
import Dropzone from "react-dropzone";
import {LoadableProps} from "../common/props/LoadableProps";
import {none, Option} from "ts-option";
import Attachment from "../../model/attachment/Attachment";
import AttachmentImagesPreview from "../image/AttachmentImagesPreview";
import ButtonTertiary from "../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import Spinner from "../../hiberUiKit/src/atoms/Spinner/Spinner";
import classNames = require("classnames");

const spinner = require("./../../../resources/hiber/img/spinner-orange.png");

interface ComponentProps {
	/**
	 * On file Drop down
	 * @param {File} file File dropped
	 */
	onDropFile: (file: File) => void;
	/**
	 * On remove file callback
	 */
	onRemove: () => void
	/**
	 * Indicates if files are uploading
	 */
	attachment: Option<Attachment>;
	/**
	 * If true, selected file cannot be removed (still can be switched with other file).
	 */
	hideRemove?: boolean;
	/**
	 * If true, border is rendered around the dropzone.
	 */
	hasBorder?: boolean;
	/**
	 * Custom text to display in dropdown.
	 */
	customText?: string;
	/**
	 * Additional information for file drop
	 */
	dropzoneAdditionalInfo?: Option<string>;
	/**
	 * Validate dropped file
	 */
	validator?: (file: any) => Promise<Option<string[]>>;
}

interface State {
	/**
	 * Dropped file.
	 */
	droppedFile: File;
	/**
	 * Show image
	 */
	showImage: boolean
	/**
	 * Removed indicator (for files added before)
	 */
	removed: boolean
	/**
	 * Errors
	 */
	errors?: Option<string[]>;
}

type Props = ComponentProps & LoadableProps;

export default class SingleFileDropDown extends React.Component<Props, State> {
	imageExtensions = ".png,.jpg,.jpeg,.jpe,.gif";

	constructor(props: Props) {
		super(props);
		this.state = {droppedFile: null, showImage: false, removed: false, errors: none};
		this.onDrop = this.onDrop.bind(this);
		this.clearFiles = this.clearFiles.bind(this);
		this.displayErrors = this.displayErrors.bind(this);
	}

	render() {
		const classFromProps = classNames({
			"dropzone": true,
			"has-border": this.props.hasBorder
		});
		return (
			<div className={'c-single-file-selector'}>
				<Dropzone onDrop={this.onDrop}
						  accept={this.imageExtensions}
						  className={classFromProps}
						  multiple={false}
						  activeClassName={"active"}>
					<div className={"t-link"}>{this.props.customText ||
					"Click here to select file to upload or simply drop it in."}</div>
					{this.props.dropzoneAdditionalInfo && this.props.dropzoneAdditionalInfo.isDefined &&
					<div>{this.props.dropzoneAdditionalInfo.get}</div>}
					{this.props.loading && <Spinner size={36} imgUrl={spinner}/>}
				</Dropzone>
				{this.renderAcceptedFiles()}
				{this.displayErrors()}
				{this.state.showImage &&
				<AttachmentImagesPreview attachments={[this.props.attachment.get]}
										 onClose={() => this.setState({showImage: false})}
										 ignoreExtension/>}
			</div>
		)
	}

	displayErrors() {
		return (
			!!this.state.errors.getOrElse([]).length
			&&
			<div className={'error-list'}>
				<h4>Upload errors</h4>
				{this.state.errors.map(
					errors => errors.map((e, index) => (<span key={index} className="d-block">{e}</span>))).orNull}
			</div>
		)
	}

	renderAcceptedFiles() {
		return (
			((this.state.droppedFile || this.props.attachment.isDefined) && !this.state.removed) &&
			<div>
				<h4>File selected</h4>
				<div style={{display: "inline"}}>{this.getFileName()}</div>
				<div style={{display: "inline"}}>
					{!this.props.hideRemove && <ButtonTertiary label={"Remove"} onClick={() => {this.clearFiles()}}/>}
				</div>
				{this.renderImagePreview()}
			</div>
		)
	}

	renderImagePreview() {
		if (!this.state.droppedFile && this.props.attachment.isDefined) {
			return <div style={{display: "inline"}}><ButtonTertiary label={"Show image"}
																	onClick={() => { this.setState({showImage: true})}}/></div>
		}
		return null;
	}

	getFileName() {
		return this.state.droppedFile ? this.state.droppedFile.name : this.props.attachment.map(a => a.name).getOrElse("");
	}

	onStandardDropActivity(droppedFile: any) {
		this.setState({droppedFile, removed: false});
		this.props.onDropFile(droppedFile);
	};

	onDrop(acceptedFiles: any[]) {
		const [droppedFile] = acceptedFiles;
		this.setState({droppedFile: null, errors: none}, () => {
			if (droppedFile && this.props.validator) {
				this.props.validator(droppedFile).then(() => {
					this.onStandardDropActivity(droppedFile);
				}).catch((errors) => {
					this.setState({errors});
				});
			}
			else {
				this.onStandardDropActivity(droppedFile);
			}
		});

	}

	clearFiles() {
		this.setState({droppedFile: null, removed: true}, () => {
			this.props.onRemove();
		});
	}
}